/**
 * Leaflet layers etc, can not be just assigned to another map because they refer to DOM elements
 * So we need to use this library to clone them.
 * Library is relatively old and has no typings, so including them here instead of importing.
 * See: https://github.com/jieter/leaflet-clonelayer
 * @param options
 */

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import * as L from 'leaflet';
import 'proj4leaflet';
import NonTiledLayer from './nontiledlayer';

function createUntiledWmsOverlay(
  map: L.Map,
  layer: NonTiledLayer.WMS,
): L.ImageOverlay {
  const size = map.getSize();
  const bboxArray = map
    .getBounds()
    .toBBoxString()
    .split(',')
    .map((i: string) => parseFloat(i));

  const p1 = layer._crs.project({
    lat: bboxArray[1],
    lng: bboxArray[0],
  });
  // project second coord pair
  const p2 = layer._crs.project({
    lat: bboxArray[3],
    lng: bboxArray[2],
  });
  const projectedBbox = [p1.x, p1.y, p2.x, p2.y].join(',');

  const url =
    `${layer._wmsUrl}?service=WMS&version=1.3.0&request=GetMap` +
    `&layers=${layer.wmsParams.layers ?? layer.options.layers}&styles=${layer.wmsParams.styles ?? layer.options.styles ?? ''}&format=image/png&transparent=true` +
    `&width=${size.x}&height=${size.y}&crs=${layer._crs.code}&bbox=${projectedBbox}` +
    `&format_options&cql_filter=${layer.wmsParams.cql_filter ?? ''}`;

  // project first coord pair back to LatLng
  const nw = layer._crs.unproject(L.point(p1.x, p2.y));
  // project second coord pair back to LatLng
  const se = layer._crs.unproject(L.point(p2.x, p1.y));

  return L.imageOverlay(url, L.latLngBounds(nw, se), {
    interactive: false,
    zIndex: layer.options.zIndex,
    crossOrigin: true,
    opacity: layer.options.opacity,
  });
}

function cloneOptions(options: any, newMap: L.Map): any {
  const ret: any = {};
  for (const i in options) {
    const item = options[i];
    if (item && typeof item.clone === 'function') {
      ret[i] = item.clone();
    } else if (item instanceof L.Layer) {
      ret[i] = cloneLayer(item, newMap);
    } else {
      ret[i] = item;
    }
  }
  return ret;
}

/**
 * Deep clones a Leaflet layer (for use in multiple maps).
 * Supports most core Leaflet layer types and some common plugins.
 */
export function cloneLayer(layer: L.Layer, newMap: L.Map): L.Layer | null {
  const options = cloneOptions((layer as any).options || {}, newMap);

  // Renderers
  if (layer instanceof L.SVG) return L.svg(options);
  if (layer instanceof L.Canvas) return L.canvas(options);

  // Tile layers
  if (layer instanceof L.TileLayer.WMS) {
    return L.tileLayer.wms((layer as any)._url, options);
  }
  if (layer instanceof L.TileLayer) {
    return L.tileLayer((layer as any)._url, options);
  }

  // Image overlays
  if (layer instanceof L.ImageOverlay) {
    return L.imageOverlay((layer as any)._url, layer.getBounds(), options);
  }

  // Marker and shape layers
  if (layer instanceof L.Marker) return L.marker(layer.getLatLng(), options);
  if (layer instanceof L.Circle)
    return L.circle(layer.getLatLng(), layer.getRadius(), options);
  if (layer instanceof L.CircleMarker)
    return L.circleMarker(layer.getLatLng(), options);
  if (layer instanceof L.Rectangle)
    return L.rectangle(layer.getBounds(), options);
  if (layer instanceof L.Polygon) return L.polygon(layer.getLatLngs(), options);
  if (layer instanceof L.Polyline) {
    return L.polyline(layer.getLatLngs(), options);
  }

  // GeoJSON
  if (layer instanceof L.GeoJSON) {
    return L.geoJSON(layer.toGeoJSON(), options);
  }

  // Layer groups (ignore, not necessary for our application)
  if (layer instanceof L.FeatureGroup) {
    return null;
  }
  if (layer instanceof L.LayerGroup) {
    return null;
  }

  // non-tiled WMS layer
  // for some reason, non-tiled WMS layers couldn't be cloned
  // so instead, we're just creating an image overlay that captures the same area
  if (layer instanceof NonTiledLayer.WMS) {
    return createUntiledWmsOverlay(newMap, layer);
  }

  throw new Error('Unsupported layer type: ' + layer.constructor.name);
}
